import cx from 'classnames'
import { Field } from 'formik'
import isArray from 'lodash/isArray'
import { useEffect, useState } from 'react'
import TagsInput from 'react-tagsinput'
import { customReportsApi } from '~/api/reports'
import tagsStyles from '~/common/TagsInput.module.scss'
import { JSFSelect } from '~/components/jsonschema/VndlyJsonSchemaFormBase/widgets/select'
import { FormikVndlyBusinessUnitPickerv2 } from '~/components/v2/VndlyBusinessUnitPicker'
import styles from '~/components/VndlyBusinessUnitPicker.module.scss'
import { DatasetFieldInput } from '~/custom_reports/builder/FormFields'
import { ReportingDatePicker } from '~/custom_reports/builder/ReportingDatePicker'
import { isCalculatedField, isParameterField, isSystemVariableField } from '~/custom_reports/shared'
import { onKeyDownHandler } from '~/custom_reports/utils'
import { Input } from '~/design_system'
import { useCustomReportBuilderStore } from '~/custom_reports/builder/state'
import VndlySelect from '../../components/VndlySelect'

const INPUT_TYPES = { input: 'text', number: 'number' }

export const FilterValue = ({
  name,
  filter,
  setFieldValue,
  parameterAsValue = false,
  omittedFilterValues = null,
  isInModal = false
}) => {
  const _actions = useCustomReportBuilderStore(state => state.actions)
  const actions = {
    getField: _actions.getField,
    getDatasetId: _actions.getDatasetId,
    getFieldType: _actions.getFieldType,
    getFilterType: _actions.getFilterType,
    getFilterMetadata: _actions.getFilterMetadata
  }
  const [filterOptions, setFilterOptions] = useState(null)
  const inputInModalProps = isInModal ? { onKeyDown: onKeyDownHandler } : {}

  useEffect(() => {
    if (
      !isCalculatedField(filter) &&
      !isParameterField(filter) &&
      !isSystemVariableField(filter) &&
      actions.getField(filter).filter_type === 'options'
    ) {
      const field = actions.getField(filter)
      const datasetId = actions.getDatasetId(filter.path)
      if (omittedFilterValues?.name === field.id) {
        const omittedIds = omittedFilterValues?.ids || []
        customReportsApi.getFilterOptions(datasetId, field.id, omittedIds).then(setFilterOptions)
      } else {
        customReportsApi.getFilterOptions(datasetId, field.id).then(setFilterOptions)
      }
    }
  }, [filter])
  const fieldType = actions.getFieldType(filter)
  const filterType = actions.getFilterType(filter)

  // check if 'use parameter' is toggled before determining component based on filterType
  if (parameterAsValue) {
    return (
      <>
        <DatasetFieldInput
          field={{ path: '__parameter__' }}
          name={name}
          predicate={f => f.filter_type || f.data_type}
        />
      </>
    )
  }

  if (filterType === 'options') {
    return (
      <>
        {filterOptions && (
          <Field
            name={name}
            component={VndlySelect}
            options={filterOptions}
            value={filter.value}
            menuPortalTarget={document.body}
            isMulti
            style={{ height: 'unset !important' }}
            {...inputInModalProps}
          />
        )}
      </>
    )
  }

  if (filterType === 'bu_picker') {
    return <BusinessUnitPickerFilterValue setFieldValue={setFieldValue} fieldName={name} value={filter.value} />
  }

  const isMultiInput =
    ['any_of', 'not_any_of'].includes(filter.operator) ||
    (['includes', 'includes_any', 'not_includes'].includes(filter.operator) && fieldType === 'array')

  if (filterType === 'jsf_select') {
    const filterMetadata = actions.getFilterMetadata(filter)
    return (
      <JSFSelect
        name={name}
        value={filter.value}
        isClearable
        options={filterMetadata.options}
        menuPortalTarget={document.body}
        onChange={values => setFieldValue(name, values)}
        style={{ width: '100%', height: 'unset !important' }}
        multiple={isMultiInput}
        {...inputInModalProps}
      />
    )
  }

  const isInputFieldType = ['integer', 'number', 'string', 'text', 'array'].indexOf(fieldType) !== -1
  const isDateFieldType = ['date', 'datetime'].indexOf(fieldType) !== -1

  const isInputDateField = isDateFieldType && filter.aggregation === 'string_agg'
  const isDatePickerField = isDateFieldType && filter.aggregation !== 'string_agg'

  return (
    <>
      {(isInputFieldType || isInputDateField) && !isMultiInput && (
        <Field
          name={name}
          type={INPUT_TYPES[filterType] || 'text'}
          placeholder="Filter Value"
          as={Input}
          {...inputInModalProps}
        />
      )}
      {(isInputFieldType || isInputDateField) && isMultiInput && (
        <TagsInput
          className={tagsStyles['react-tagsinput']}
          tagProps={{
            className: tagsStyles['react-tagsinput-tag'],
            classNameRemove: tagsStyles['react-tagsinput-remove']
          }}
          inputProps={{
            placeholder: 'Enter multiple values',
            className: cx(tagsStyles['react-tagsinput-input'], styles.TagsInput)
          }}
          value={isArray(filter.value) || !filter.value ? filter.value || [] : [filter.value]}
          onChange={values => setFieldValue(name, values)}
          addOnPaste
          pasteSplit={data => {
            const separators = [',', ';', '\\(', '\\)', '\\*', '/', ':', '\\?', '\r\n', '\n', '\r']
            return data.split(new RegExp(separators.join('|'))).map(d => d.trim())
          }}
        />
      )}
      {isDatePickerField && (
        <ReportingDatePicker name={name} setFieldValue={setFieldValue} isDatePickerInModal={isInModal} />
      )}
    </>
  )
}

const BusinessUnitPickerFilterValue = ({ fieldName, value, setFieldValue }) => {
  const [originalFieldValue] = useState(value || [])

  return (
    <FormikVndlyBusinessUnitPickerv2
      value={originalFieldValue}
      minLevel="2"
      isMulti
      onChange={buList =>
        setFieldValue(
          fieldName,
          (buList || { value: [] }).value.map(v => v.id)
        )
      }
    />
  )
}
